import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { authorizedFetch } from '@/lib/api'
import { API_ORIGIN } from '@/constants'
import { jobProspectMatchesQueryKey } from '@/hooks/use-job-prospect-matches'
import { jobTalentSummaryQueryKey } from '@/hooks/use-job-talent-summary'
import { FIT_POLL_MS } from '@/lib/analysis-wait'
import { useFitRetryPending } from '@/hooks/use-fit-retry-pending'
import { isActiveApplicationStatus } from '@/utils/application-status'

export function opportunityApplicantsQueryKey(opportunityId: string) {
  return ['opportunity-applicants', opportunityId] as const
}

export interface Stage {
  slug: string
  name: string
  order: number
  is_terminal: boolean
}

export interface ApplicantProfileSummary {
  id: string
  name: string
  headline: string | null
  avatar_initials: string | null
  avatar_url?: string | null
  phone?: string | null
  website?: string | null
  linkedin_url?: string | null
  x_url?: string | null
  latest_role?: string | null
  top_skills?: string[]
  city?: string | null
  country?: string | null
  experience_companies?: string[]
  open_to_work?: boolean
}

/** Maps Django applicant profile payload (`full_name`, snake_case extras) to Labs shape. */
export function normalizeApplicantProfileFromApi(raw: Record<string, unknown>): ApplicantProfileSummary {
  const fullName = typeof raw.full_name === 'string' ? raw.full_name.trim() : ''
  const legacyName = typeof raw.name === 'string' ? raw.name.trim() : ''
  const au = raw.avatar_url
  const avatar_url = typeof au === 'string' && au.trim() ? au.trim() : null
  const lr = raw.latest_role
  const latest_role = typeof lr === 'string' && lr.trim() ? lr.trim() : null
  const phoneRaw = raw.phone
  const phone = typeof phoneRaw === 'string' && phoneRaw.trim() ? phoneRaw.trim() : null
  const websiteRaw = raw.website
  const website = typeof websiteRaw === 'string' && websiteRaw.trim() ? websiteRaw.trim() : null
  const linkedinRaw = raw.linkedin_url
  const linkedin_url = typeof linkedinRaw === 'string' && linkedinRaw.trim() ? linkedinRaw.trim() : null
  const xRaw = raw.x_url
  const x_url = typeof xRaw === 'string' && xRaw.trim() ? xRaw.trim() : null
  const topSkillsRaw = raw.top_skills
  const top_skills = Array.isArray(topSkillsRaw)
    ? topSkillsRaw.filter((skill): skill is string => typeof skill === 'string' && skill.trim().length > 0)
    : []
  const cityRaw = raw.city
  const city = typeof cityRaw === 'string' && cityRaw.trim() ? cityRaw.trim() : null
  const countryRaw = raw.country
  const country = typeof countryRaw === 'string' && countryRaw.trim() ? countryRaw.trim() : null
  const companiesRaw = raw.experience_companies
  const experience_companies = Array.isArray(companiesRaw)
    ? companiesRaw.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
    : []
  return {
    id: typeof raw.id === 'string' ? raw.id : String(raw.id ?? ''),
    name: fullName || legacyName || 'Unknown',
    headline: typeof raw.headline === 'string' ? raw.headline : null,
    avatar_initials: typeof raw.avatar_initials === 'string' ? raw.avatar_initials : null,
    avatar_url,
    phone,
    website,
    linkedin_url,
    x_url,
    latest_role,
    top_skills,
    city,
    country,
    experience_companies,
    open_to_work: raw.open_to_work === true,
  }
}

export interface ApplicantStageRef {
  slug: string
  name: string
  order: number
  is_terminal: boolean
}

export interface ApplicantCard {
  id: string
  profile: ApplicantProfileSummary
  opportunity_id: string
  status: string
  source: string | null
  current_stage: ApplicantStageRef | null
  days_in_stage: number | null
  applied_at: string
  updated_at: string
  has_thread: boolean
  has_cover_letter?: boolean
  event_count: number
  match_score: number | null
  evaluation_id?: string | null
  board_position: number | null
  nudged_at?: string | null
}

export interface ApplicantBoardOpportunity {
  id: string
  title: string
  organisation_id: string
  organisation_name: string
  organisation_slug: string
}

export interface ApplicantBoard {
  opportunity: ApplicantBoardOpportunity
  stages: Stage[]
  applicants: ApplicantCard[]
}

async function fetchOpportunityApplicants(
  opportunityId: string,
  signal?: AbortSignal,
): Promise<ApplicantBoard> {
  const response = await authorizedFetch(
    `${API_ORIGIN}/api/applications/opportunities/${encodeURIComponent(opportunityId)}/applicants/`,
    { signal },
  )
  if (!response.ok) {
    throw new Error(`Failed to load applicants (${response.status})`)
  }
  const board = await response.json() as ApplicantBoard
  return {
    ...board,
    applicants: board.applicants.map((a) => ({
      ...a,
      profile: normalizeApplicantProfileFromApi(a.profile as unknown as Record<string, unknown>),
    })),
  }
}

export function boardHasUnscoredActiveApplicants(
  applicants: ApplicantCard[] | undefined,
): boolean {
  if (!applicants?.length) return false
  return applicants.some(
    (applicant) =>
      isActiveApplicationStatus(applicant.status) && applicant.match_score == null,
  )
}

export function useOpportunityApplicants(opportunityId: string | undefined) {
  const { waitingApplicationIds, clearApplicationPending } = useFitRetryPending()
  const query = useQuery({
    queryKey: opportunityApplicantsQueryKey(opportunityId ?? ''),
    queryFn: ({ signal }) => fetchOpportunityApplicants(opportunityId!, signal),
    enabled: !!opportunityId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: (current) =>
      boardHasUnscoredActiveApplicants(current.state.data?.applicants) ? FIT_POLL_MS : false,
  })

  useEffect(() => {
    const applicants = query.data?.applicants ?? []
    for (const id of waitingApplicationIds) {
      const row = applicants.find((applicant) => applicant.id === id)
      if (row && row.match_score != null) {
        clearApplicationPending(id)
      }
    }
  }, [clearApplicationPending, waitingApplicationIds, query.data?.applicants])

  return {
    board: query.data,
    isLoading: query.isLoading,
    /** True during initial load and background refetch — use to avoid URL/detail races while the board list is in flux. */
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  }
}

function invalidateApplicantBoardQueries(
  queryClient: QueryClient,
  opportunityId: string | undefined,
) {
  if (!opportunityId) return
  void queryClient.invalidateQueries({ queryKey: opportunityApplicantsQueryKey(opportunityId) })
  void queryClient.invalidateQueries({ queryKey: jobProspectMatchesQueryKey(opportunityId) })
  void queryClient.invalidateQueries({ queryKey: jobTalentSummaryQueryKey(opportunityId) })
}

async function fetchApplicationStages(): Promise<Stage[]> {
  const response = await authorizedFetch(`${API_ORIGIN}/api/applications/stages/`)
  if (!response.ok) {
    throw new Error(`Failed to load stages (${response.status})`)
  }
  return response.json()
}

export function useApplicationStages() {
  const query = useQuery({
    queryKey: ['application-stages'],
    queryFn: fetchApplicationStages,
    staleTime: 5 * 60 * 1000,
  })

  return {
    stages: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  }
}

interface ChangeStageVars {
  applicationId: string
  stageSlug: string
}

/** Apply board cache update synchronously (before `mutate`) so Kanban + dnd don’t paint a wrong column. Returns `previous` for rollback. */
export function applyOpportunityBoardStageChangeOptimistic(
  queryClient: QueryClient,
  opportunityId: string,
  applicationId: string,
  stageSlug: string,
): ApplicantBoard | undefined {
  const queryKey = ['opportunity-applicants', opportunityId]
  const previous = queryClient.getQueryData<ApplicantBoard>(queryKey)
  if (!previous) return undefined
  const stage = previous.stages.find((s) => s.slug === stageSlug) ?? null
  queryClient.setQueryData<ApplicantBoard>(queryKey, {
    ...previous,
    applicants: previous.applicants.map((a) =>
      a.id === applicationId
        ? {
            ...a,
            current_stage: stage
              ? {
                  slug: stage.slug,
                  name: stage.name,
                  order: stage.order,
                  is_terminal: stage.is_terminal,
                }
              : a.current_stage,
            days_in_stage: 0,
            updated_at: new Date().toISOString(),
          }
        : a,
    ),
  })
  return previous
}

export function applyOpportunityBoardOrderOptimistic(
  queryClient: QueryClient,
  opportunityId: string,
  orderings: Array<{ application_id: string; board_position: number }>,
): ApplicantBoard | undefined {
  const queryKey = ['opportunity-applicants', opportunityId]
  const previous = queryClient.getQueryData<ApplicantBoard>(queryKey)
  if (!previous) return undefined

  const positionById = new Map(orderings.map((entry) => [entry.application_id, entry.board_position]))
  queryClient.setQueryData<ApplicantBoard>(queryKey, {
    ...previous,
    applicants: previous.applicants.map((applicant) => {
      const nextPosition = positionById.get(applicant.id)
      if (nextPosition === undefined) return applicant
      return { ...applicant, board_position: nextPosition }
    }),
  })
  return previous
}

interface BoardOrderVars {
  orderings: Array<{ application_id: string; board_position: number }>
}

export function useUpdateOpportunityBoardOrder(opportunityId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderings }: BoardOrderVars) => {
      if (!opportunityId) {
        throw new Error('Missing opportunity id')
      }
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/opportunities/${encodeURIComponent(opportunityId)}/board-order/`,
        {
          method: 'POST',
          body: JSON.stringify({ orderings }),
        },
      )
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || body.detail || `Failed to update board order (${response.status})`)
      }
      return response.json() as Promise<{ updated: number }>
    },
    onMutate: async ({ orderings }) => {
      if (!opportunityId) return
      const queryKey = ['opportunity-applicants', opportunityId]
      await queryClient.cancelQueries({ queryKey })
      const previous = applyOpportunityBoardOrderOptimistic(queryClient, opportunityId, orderings)
      return { previous }
    },
    onError: (_error, _variables, context) => {
      if (!opportunityId || !context?.previous) return
      queryClient.setQueryData(['opportunity-applicants', opportunityId], context.previous)
    },
    onSuccess: (_data, { orderings }) => {
      if (!opportunityId) return
      applyOpportunityBoardOrderOptimistic(queryClient, opportunityId, orderings)
    },
    // Do not invalidate on success: optimistic cache matches the persisted order, and
    // invalidation races with in-flight refetches (e.g. post-analysis) that can revert sort.
  })
}

export function useChangeApplicantStage(opportunityId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ applicationId, stageSlug }: ChangeStageVars) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/${encodeURIComponent(applicationId)}/change-stage/`,
        {
          method: 'POST',
          body: JSON.stringify({ stage_slug: stageSlug }),
        },
      )
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || body.detail || `Failed to change stage (${response.status})`)
      }
      return response.json()
    },
    onMutate: async () => {
      if (!opportunityId) return
      const queryKey = ['opportunity-applicants', opportunityId]
      await queryClient.cancelQueries({ queryKey })
    },
    onSettled: () => {
      invalidateApplicantBoardQueries(queryClient, opportunityId)
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      void queryClient.invalidateQueries({ queryKey: ['application-stages'] })
    },
  })
}

interface BulkChangeStageVars {
  applicationIds: string[]
  stageSlug: string
}

export function useBulkChangeApplicantStage(opportunityId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ applicationIds, stageSlug }: BulkChangeStageVars) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/bulk-change-stage/`,
        {
          method: 'POST',
          body: JSON.stringify({ application_ids: applicationIds, stage_slug: stageSlug }),
        },
      )
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || body.detail || `Failed to bulk change stage (${response.status})`)
      }
      return response.json()
    },
    onSettled: () => {
      invalidateApplicantBoardQueries(queryClient, opportunityId)
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      void queryClient.invalidateQueries({ queryKey: ['application-stages'] })
    },
  })
}

interface DecisionVars {
  applicationId: string
  decision: 'hired' | 'rejected' | 'offer'
  reason?: string
}

export function useApplicantDecision(opportunityId: string | undefined) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ applicationId, decision, reason }: DecisionVars) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/${encodeURIComponent(applicationId)}/decision/`,
        {
          method: 'POST',
          body: JSON.stringify({ status: decision, reason: reason ?? null }),
        },
      )
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.error || body.detail || `Failed to record decision (${response.status})`)
      }
      return response.json()
    },
    onSettled: () => {
      invalidateApplicantBoardQueries(queryClient, opportunityId)
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
    },
  })
}
